import React, { useState, useEffect } from 'react'
import { Shield, Save, AlertTriangle, CheckCircle, Info, FileText, Database, Library, BarChart3 } from 'lucide-react'
import type { NdaSettings } from '../lib/storage'
import NdaTemplateSelector from './NdaTemplateSelector'
import NdaAnalyticsDashboard from './NdaAnalyticsDashboard'
import NdaCompanyBypass from './NdaCompanyBypass'
import SectionHeading from './SectionHeading'

interface NdaSettingsManagerProps {
  tenantId: string
  userEmail: string
}

const NdaSettingsManager: React.FC<NdaSettingsManagerProps> = ({ tenantId, userEmail }) => {
  const [settings, setSettings] = useState<Partial<NdaSettings>>({
    enforcementMode: 'optional',
    clickwrapEnabled: false,
    clickwrapTitle: 'Non-Disclosure Agreement',
    clickwrapText: '',
    clickwrapVersion: '1.0',
    crmCheckEnabled: false,
    crmProvider: null,
    crmFallbackBehavior: 'clickwrap',
    applyToAllDocuments: false
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showTemplates, setShowTemplates] = useState(false)
  const [showAnalytics, setShowAnalytics] = useState(false)

  const fetchSettings = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`/api/nda/settings?tenantId=${tenantId}`)
      if (!response.ok) throw new Error('Failed to fetch NDA settings')

      const data = await response.json()
      setSettings(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load NDA settings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSettings()
  }, [tenantId])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const token = localStorage.getItem('auth-token')
      const response = await fetch(`/api/nda/settings?tenantId=${tenantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...settings,
          updatedBy: userEmail
        })
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        try {
          const data = await response.json()
          errorMessage = data.error || errorMessage
        } catch (parseErr) {
          console.error('[NdaSettingsManager] Failed to parse error response:', parseErr)
          // Use generic message
        }
        console.error('[NdaSettingsManager] Save failed:', errorMessage)
        throw new Error(errorMessage)
      }

      const data = await response.json()
      setSettings(data.settings)
      setSuccess('NDA settings saved successfully')

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000)

    } catch (err: any) {
      setError(err.message || 'Failed to save NDA settings')
    } finally {
      setSaving(false)
    }
  }

  const defaultNdaText = `# Non-Disclosure Agreement

## 1. Purpose

This Non-Disclosure Agreement ("Agreement") is entered into to protect the confidential and proprietary information that may be disclosed during the course of evaluating our products and services.

## 2. Confidential Information

"Confidential Information" includes, but is not limited to:
- Technical documentation and specifications
- Security policies and procedures
- Compliance certifications and audit reports
- Product roadmaps and feature plans
- Customer information and usage data
- Any information marked as "Confidential" or "Proprietary"

## 3. Obligations

You agree to:
- Maintain the confidentiality of all Confidential Information
- Use the information solely for evaluation purposes
- Not disclose or distribute information to third parties without prior written consent
- Protect the information with the same degree of care as your own confidential information

## 4. Exclusions

This Agreement does not apply to information that:
- Is or becomes publicly available through no breach of this Agreement
- Was rightfully in your possession prior to disclosure
- Is independently developed without use of Confidential Information
- Is required to be disclosed by law or court order

## 5. Term

This Agreement remains in effect for the duration of your access to our Trust Center and for 2 years thereafter.

## 6. Return of Materials

Upon request or termination of access, you agree to return or destroy all Confidential Information and certify such destruction in writing.

By accepting this Agreement, you acknowledge that you have read, understood, and agree to be bound by its terms.`

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <SectionHeading id="nda-settings" className="text-2xl font-bold text-gray-900">
            <>
              <Shield className="h-6 w-6 text-primary-600 mr-3" />
              NDA Settings
            </>
          </SectionHeading>
          <button
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
          >
            <BarChart3 className="h-4 w-4" />
            {showAnalytics ? 'Hide Analytics' : 'View Analytics'}
          </button>
        </div>
        <p className="text-gray-600">
          Configure Non-Disclosure Agreement requirements for document access. You can require NDAs via clickwrap acceptance, CRM validation, or both.
        </p>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <NdaAnalyticsDashboard tenantId={tenantId} />
        </div>
      )}

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-900">Error</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-green-900">Success</p>
            <p className="text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Enforcement Mode */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Enforcement Mode</h3>

        <div className="space-y-3">
          <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                 style={{ borderColor: settings.enforcementMode === 'disabled' ? '#3b82f6' : '#d1d5db' }}>
            <input
              type="radio"
              name="enforcementMode"
              value="disabled"
              checked={settings.enforcementMode === 'disabled'}
              onChange={(e) => setSettings({ ...settings, enforcementMode: 'disabled' })}
              className="mt-1 h-4 w-4 text-blue-600"
            />
            <div>
              <p className="font-medium text-gray-900">Disabled</p>
              <p className="text-sm text-gray-600">No NDA required for document access</p>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                 style={{ borderColor: settings.enforcementMode === 'optional' ? '#3b82f6' : '#d1d5db' }}>
            <input
              type="radio"
              name="enforcementMode"
              value="optional"
              checked={settings.enforcementMode === 'optional'}
              onChange={(e) => setSettings({ ...settings, enforcementMode: 'optional' })}
              className="mt-1 h-4 w-4 text-blue-600"
            />
            <div>
              <p className="font-medium text-gray-900">Optional (Document-Level)</p>
              <p className="text-sm text-gray-600">Only documents marked as "NDA Required" will enforce NDA</p>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                 style={{ borderColor: settings.enforcementMode === 'required' ? '#3b82f6' : '#d1d5db' }}>
            <input
              type="radio"
              name="enforcementMode"
              value="required"
              checked={settings.enforcementMode === 'required'}
              onChange={(e) => setSettings({ ...settings, enforcementMode: 'required' })}
              className="mt-1 h-4 w-4 text-blue-600"
            />
            <div>
              <p className="font-medium text-gray-900">Required (All Documents)</p>
              <p className="text-sm text-gray-600">NDA required for all document access</p>
            </div>
          </label>
        </div>

        {settings.enforcementMode === 'optional' && (
          <div className="mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.applyToAllDocuments || false}
                onChange={(e) => setSettings({ ...settings, applyToAllDocuments: e.target.checked })}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">Apply to all documents (override individual settings)</span>
            </label>
          </div>
        )}
      </div>

      {/* Clickwrap Configuration */}
      {settings.enforcementMode !== 'disabled' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Clickwrap NDA</h3>
          </div>

          <label className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              checked={settings.clickwrapEnabled || false}
              onChange={(e) => setSettings({ ...settings, clickwrapEnabled: e.target.checked })}
              className="h-4 w-4 text-blue-600 rounded"
            />
            <span className="text-sm font-medium text-gray-900">Enable clickwrap NDA acceptance</span>
          </label>

          {settings.clickwrapEnabled && (
            <div className="space-y-4 pl-6 border-l-2 border-blue-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">NDA Title</label>
                <input
                  type="text"
                  value={settings.clickwrapTitle || ''}
                  onChange={(e) => setSettings({ ...settings, clickwrapTitle: e.target.value })}
                  placeholder="Non-Disclosure Agreement"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    NDA Text (Markdown supported)
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowTemplates(!showTemplates)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 hover:border-blue-400 rounded-md transition-colors"
                  >
                    <Library className="h-4 w-4" />
                    {showTemplates ? 'Hide Templates' : 'Browse Templates'}
                  </button>
                </div>

                {showTemplates && (
                  <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <NdaTemplateSelector
                      currentText={settings.clickwrapText || ''}
                      currentTitle={settings.clickwrapTitle || ''}
                      onSelect={(title, text) => {
                        setSettings({ ...settings, clickwrapTitle: title, clickwrapText: text })
                        setShowTemplates(false)
                      }}
                    />
                  </div>
                )}

                <textarea
                  value={settings.clickwrapText || ''}
                  onChange={(e) => setSettings({ ...settings, clickwrapText: e.target.value })}
                  placeholder={defaultNdaText}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
                {(!settings.clickwrapText || settings.clickwrapText.trim() === '') && (
                  <button
                    type="button"
                    onClick={() => setSettings({ ...settings, clickwrapText: defaultNdaText })}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    Use default template
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
                  <input
                    type="text"
                    value={settings.clickwrapVersion || ''}
                    onChange={(e) => setSettings({ ...settings, clickwrapVersion: e.target.value })}
                    placeholder="1.0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Users must re-accept if version changes</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry (days) <span className="text-gray-500 font-normal">(optional)</span>
                  </label>
                  <input
                    type="number"
                    value={settings.clickwrapExpiryDays || ''}
                    onChange={(e) => setSettings({ ...settings, clickwrapExpiryDays: e.target.value ? parseInt(e.target.value) : undefined })}
                    placeholder="Never expires"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for no expiration</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CRM Integration */}
      {settings.enforcementMode !== 'disabled' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Database className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">CRM NDA Check</h3>
          </div>

          <label className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              checked={settings.crmCheckEnabled || false}
              onChange={(e) => setSettings({ ...settings, crmCheckEnabled: e.target.checked })}
              className="h-4 w-4 text-blue-600 rounded"
            />
            <span className="text-sm font-medium text-gray-900">Check CRM for existing NDA before requiring clickwrap</span>
          </label>

          {settings.crmCheckEnabled && (
            <div className="space-y-4 pl-6 border-l-2 border-green-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CRM Provider</label>
                <select
                  value={settings.crmProvider || ''}
                  onChange={(e) => setSettings({ ...settings, crmProvider: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select provider...</option>
                  <option value="salesforce">Salesforce</option>
                  <option value="hubspot">HubSpot (Coming Soon)</option>
                  <option value="custom">Custom API (Coming Soon)</option>
                </select>
              </div>

              {settings.crmProvider === 'salesforce' && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700">
                      Salesforce integration uses the existing configuration from System Settings. The system will check for active NDA records linked to the user's company.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fallback Behavior (if CRM check fails)</label>
                    <select
                      value={settings.crmFallbackBehavior || 'clickwrap'}
                      onChange={(e) => setSettings({ ...settings, crmFallbackBehavior: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="clickwrap">Show clickwrap NDA</option>
                      <option value="allow">Allow access (not recommended)</option>
                      <option value="deny">Deny access</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Company Bypasses */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Bypasses</h3>
        <NdaCompanyBypass tenantId={tenantId} />
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
        <button
          onClick={fetchSettings}
          disabled={saving}
          className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
        >
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-md hover:from-primary-700 hover:to-primary-800 font-medium transition-all disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default NdaSettingsManager
